import {computed, ref} from 'vue';
import axios from 'axios';
import {AsyncParser} from '@json2csv/whatwg';
import {
    initialRevenueTable,
    initialServiceSales,
    initialServicesRendered,
    initialSmogSales
} from "@utilities/reports/tables.ts";
import {formatDateYYYYMMDD} from "@utilities/helpers.ts";

const {baseURL, nonce} = localized;

// <editor-fold desc="Helper Functions">---------------------------------

const getTotalAmount = (table) => table.reduce((acc, row) => acc + Number(row.amount), 0);

const getTotalSales = (table) => table.reduce((acc, row) => {
    return Number.isFinite(acc + (Number(row.price) * Number(row.amount))) ? acc + (Number(row.price) * Number(row.amount)) : 0;
}, 0);

const clearTable = (table) => table.forEach((row) => (row.amount = 0));

const makeTableOfAmounts = (table) => {
    let result = {};
    table.forEach(item => {
        result[item.type] = item.amount;
    });
    return result;
}

// </editor-fold>--------------------------------------------------------

/**
 * Overarching composable function, declares exported state and methods
 * Requires refs or computed properties to be passed in
 * @param {import('vue').Ref<number>} shopId - The ID of the shop
 * @param {import('vue').Ref<string|Date>} date - The date for the EOD report
 */
export function useEOD(shopId, date) {

    // <editor-fold desc="Gatekeeper">---------------------------------

    if (!shopId.value) {
        console.error('No shop ID provided to useEOD');
        return;
    }

    if (!date.value) {
        console.error('No date provided to useEOD');
        return;
    }

    // Format Date
    const formattedDate = computed(() => formatDateYYYYMMDD(date.value));

    // </editor-fold>--------------------------------------------------

    // <editor-fold desc="Reactive State">-----------------------------

    const techsOnDuty = ref([]);
    const editing = ref(false);
    const serviceSales = ref(initialServiceSales);
    const servicesRendered = ref(initialServicesRendered);
    const smogSales = ref(initialSmogSales);
    const revenueTable = ref(initialRevenueTable);
    const totalCash = ref(0);
    const totalCC = ref(0);
    const smogRejects = ref(0); // Added for EOD report data, assuming it's a number
    const eodSubmitted = ref(false)

    // </editor-fold>--------------------------------------------------

    // <editor-fold desc="Computed Properties">------------------------

    const totalServices = computed(() => getTotalAmount(serviceSales.value));
    const totalServiceSales = computed(() => getTotalSales(serviceSales.value));
    const totalSmogs = computed(() => getTotalAmount(smogSales.value));
    const totalSmogSales = computed(() => getTotalSales(smogSales.value));
    const totalSales = computed(() => totalServiceSales.value + totalSmogSales.value);
    const totalRev = computed(() => getTotalSales(revenueTable.value));
    const difference = computed(() => totalRev.value - totalSales.value);

    // </editor-fold>--------------------------------------------------

    async function checkIfSubmitted() {

        const response = await axios.get(`${baseURL}reports/eods-submitted`, {
            params: {
                date_start: formattedDate.value,
                date_end: formattedDate.value,
                shop_id: shopId.value
            }
        })
            .then((response) => {
                return response.data;
            })
            .catch((error) => {
                console.error('Error checking if EOD is submitted:', error);
                return null;
            });
        if (!response) {
            console.error('Error checking if EOD is submitted');
            return null;
        }

        /* Why Check for Array?
         * In php, associative arrays are also just arrays, so if it is empty, it will arrive here as an empty array
         */
        if (Array.isArray(response)) {
            return false;
        }

        /* Checking for Submission
         * However, if there are results, then it will be represented as an object with store ids as the keys, and arrays of dates submitted as the values
         */
        const isSubmitted = response[shopId.value].includes(formattedDate.value)
        eodSubmitted.value = isSubmitted
        return isSubmitted;
    }

    /**
     * Fetches the EOD report for a given date and shop ID.
     * Updates the reactive state with the fetched data.
     */
    async function getEODReport() {

        // <editor-fold desc="Gatekeeper">---------------------------------


        const submitted = await checkIfSubmitted();
        if (!submitted) {
            console.log('EOD report not submitted for this date and shop ID.');
            techsOnDuty.value = [];
            clearTable(serviceSales.value);
            clearTable(smogSales.value);
            clearTable(servicesRendered.value);
            clearTable(revenueTable.value);
            smogRejects.value = 0;
            return;
        }

        // </editor-fold>--------------------------------------------------

        try {
            // Get the data
            const response = await axios.get(`${baseURL}reports/eod`, {
                params: {date: formattedDate.value, shop_id: shopId.value},
            });

            // If no Report Found
            if (response.status === 404) {
                console.error('EOD Report not found for the given date and shop ID.');
                clearTable(serviceSales.value);
                clearTable(smogSales.value);
                clearTable(servicesRendered.value);
                clearTable(revenueTable.value);
                return;
            }

            // Populate tables
            serviceSales.value = response.data.serviceSales;
            smogSales.value = response.data.smogSales;
            servicesRendered.value = response.data.servicesRendered;
            revenueTable.value = response.data.revenue;

            // Populate totals
            smogRejects.value = response.data.smog_rejects || 0;
            techsOnDuty.value = response.data.techs.map((tech) => tech.ID);

        } catch (error) {
            console.error('Error fetching EOD report:', error);
        }
    }

    /**
     * Submits the EOD report data to the server.
     */
    async function postEODReport(eod) {
        const data = {date: formattedDate.value, eod};

        try {
            await axios.post(`${baseURL}reports/eod`, data, {
                headers: {
                    'X-WP-Nonce': nonce,
                },
            });
            console.log('EOD Report successfully submitted');
            await getEODReport();
        } catch (error) {
            console.error('Error submitting EOD report:', error);
        }
    }

    /*
        * Saves the EOD report data by calling postEODReport.
        * Also prepares data.
     */
    async function saveEOD() {
        const eod = {
            sales: [...serviceSales.value,
                ...servicesRendered.value,
                ...smogSales.value],
            revenue: makeTableOfAmounts(revenueTable.value),
            smog_rejects: smogRejects.value,
            shop_id: shopId.value,
            techs_on_duty: techsOnDuty.value,
        }

        await postEODReport(eod);
        editing.value = false;
    }

    // Adds a row to an EOD table
    function addRow(tableName, type, price) {

        // Table object to allow tables to be accessed by string
        const tables = {
            serviceSales,
            servicesRendered,
            smogSales,
            revenueTable,
        };

        // check if the table exists
        if (!tables[tableName]) {
            console.error(`Table ${tableName} does not exist.`);
            return;
        }

        // create the row to add
        const row = {
            type: type,
            amount: 0,
        }
        if (price) {
            row['price'] = price;
        }

        // add the row
        const table = tables[tableName].value;

        if (Array.isArray(table)) {
            table.push(row);
        } else {
            console.error(`Invalid table specified while adding row: ${tableName}`);
        }
    }

    /**
     * Exports the current EOD report data as a CSV file with separated tables.
     * Uses AsyncParser with streams to avoid blocking the JS event loop.
     */
    async function exportEODToCSV() {
        try {

            // Prepare all data sections
            const allSections = [];

            // <editor-fold desc="Summary Section">--------------------------------------
            const summaryFields = ['Metric', 'Value'];
            const summaryData = [
                { Metric: 'Total Cash', Value: totalCash.value },
                { Metric: 'Total CC', Value: totalCC.value },
                { Metric: 'Total Services', Value: totalServices.value },
                { Metric: 'Total Service Sales', Value: totalServiceSales.value },
                { Metric: 'Total Smogs', Value: totalSmogs.value },
                { Metric: 'Total Smog Sales', Value: totalSmogSales.value },
                { Metric: 'Total Sales', Value: totalSales.value },
                { Metric: 'Total Revenue', Value: totalRev.value },
                { Metric: 'Difference', Value: difference.value },
                { Metric: 'Smog Rejects', Value: smogRejects.value },
            ];
            allSections.push({ title: 'Summary', data: summaryData, fields: summaryFields });
            // </editor-fold>--------------------------------------------------------------------

            // <editor-fold desc="Service Sales Section">--------------------------------------
            const serviceSalesFields = ['Price', 'Amount', 'Total'];
            const serviceSalesData = serviceSales.value.map(item => ({
                Price: item.price,
                Amount: item.amount,
                Total: item.price * item.amount
            }));
            allSections.push({ title: 'Service Sales', data: serviceSalesData, fields: serviceSalesFields });
            // </editor-fold>--------------------------------------------------------------------

            // <editor-fold desc="Services Rendered Section (Service Types)">--------------------------------------
            const servicesRenderedFields = ['Type', 'Amount'];
            const servicesRenderedData = servicesRendered.value.map(item => ({
                Type: item.type,
                Amount: item.amount
            }));
            allSections.push({ title: 'Services Rendered', data: servicesRenderedData, fields: servicesRenderedFields });
            // </editor-fold>--------------------------------------------------------------------

            // <editor-fold desc="Smog Sales Section (Smog Checks)">--------------------------------------
            const smogSalesFields = ['Price', 'Amount', 'Total'];
            const smogSalesData = smogSales.value.map(item => ({
                Price: item.price,
                Amount: item.amount,
                Total: item.price * item.amount
            }));
            allSections.push({ title: 'Smog Checks', data: smogSalesData, fields: smogSalesFields });
            // </editor-fold>--------------------------------------------------------------------

            // <editor-fold desc="Revenue Section">--------------------------------------
            const revenueFields = ['Type', 'Amount', 'Value'];
            const revenueData = revenueTable.value.map(item => ({
                Type: `${item.type}`,
                Amount: item.amount,
                Value: item.price * item.amount
            }));
            allSections.push({ title: 'Revenue', data: revenueData, fields: revenueFields });
            // </editor-fold>--------------------------------------------------------------------

            // Process each section sequentially
            let csvContent = '';
            for (let i = 0; i < allSections.length; i++) {
                const section = allSections[i];

                // Add section title with spacing
                if (i > 0) {
                    csvContent += '\n';
                }
                csvContent += section.title + '\n';

                // Create a parser for this section with a proper transform stream configuration
                const parser = new AsyncParser(
                    { fields: section.fields },  // opts
                    {},                          // asyncOpts
                    {},                          // writableStrategy
                    {}                           // readableStrategy
                );

                // Create a writable stream for this section
                const sectionChunks = [];
                const sectionWritable = new WritableStream({
                    write(chunk) {
                        sectionChunks.push(chunk);
                    }
                });

                // Process the data through the parser
                await parser.parse(JSON.stringify(section.data)).pipeTo(sectionWritable);

                // Add the section CSV to the content
                csvContent += sectionChunks.join('');

                // Add a blank line after each section (except the last one)
                if (i < allSections.length - 1) {
                    csvContent += '\n\n';
                }
            }

            // Create and trigger download
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', `eod_report_${formattedDate.value}_shop_${shopId.value}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting EOD to CSV:', error);
        }
    }

    // **Return the Public API**
    return {
        techsOnDuty,
        serviceSales,
        servicesRendered,
        smogSales,
        revenueTable,
        editing,
        totalCash,
        totalCC,
        addRow,
        smogRejects,
        totalServices,
        totalServiceSales,
        totalSmogs,
        totalSmogSales,
        totalSales,
        totalRev,
        difference,
        eodSubmitted,
        checkIfSubmitted,
        getEODReport,
        saveEOD,
        exportEODToCSV
    };
}
